import type { CatalogEntry } from '../types';

/**
 * Template Library (feature `18`) error codes + catalog.
 *
 * Code shape is `18AAEE` (decimal, zero-padded):
 *   - `AA` = endpoint within the feature (`00` = feature-level / cross-endpoint).
 *   - `EE` = specific condition within that endpoint.
 *
 * `Shared` codes (`1800xx`, AA=00) are recurring cross-endpoint conditions —
 * `CollectionNotFound` / `TemplateNotFound` — collapsed to ONE code each so the
 * same condition reads the same on every collection/template-touching route.
 *
 * Endpoint → `AA` map:
 *   createCollection=01, listCollections=02, getCollection=03, updateCollection=04,
 *   deleteCollection=05, createTemplate=06, updateTemplate=07, deleteTemplate=08.
 */
export const TEMPLATE_LIBRARY_CODES = {
  /** Cross-endpoint conditions (AA=00). */
  Shared: {
    CollectionNotFound: '180000',
    TemplateNotFound: '180001',
  },
  /** createCollection=01. */
  CreateCollection: {
    NameInvalid: '180101',
    NameTaken: '180102',
    LimitReached: '180103',
  },
  /** createTemplate=06. */
  CreateTemplate: {
    ContentFlagged: '180601',
    LimitReached: '180602',
    PhoneNumberTagRequired: '180603',
    UnknownMergeTag: '180604',
  },
  /** updateTemplate=07. */
  UpdateTemplate: {
    ContentFlagged: '180701',
    PhoneNumberTagRequired: '180702',
    UnknownMergeTag: '180703',
  },
} as const;

export const TEMPLATE_LIBRARY_CATALOG: Record<string, CatalogEntry> = {
  // ── Shared (cross-endpoint) ──
  '180000': { httpStatus: 404, message: 'Template collection not found.' },
  '180001': { httpStatus: 404, message: 'Template not found.' },

  // ── Create collection ──
  '180101': {
    httpStatus: 400,
    message: 'Collection names must be between 1 and 100 characters.',
  },
  '180102': {
    httpStatus: 409,
    message: 'A collection with that name already exists.',
  },
  '180103': {
    httpStatus: 409,
    message: "You've reached the template collection limit for this workspace.",
  },

  // ── Create template ──
  '180601': {
    httpStatus: 400,
    message: "This template couldn't be saved because it may contain flagged content. Please revise and try again.",
  },
  '180602': {
    httpStatus: 409,
    message: "You've reached the template limit for this collection.",
  },
  '180603': {
    httpStatus: 400,
    message: "Add the {{phone number}} merge tag to your template's body before saving.",
  },
  '180604': {
    httpStatus: 400,
    message: "This template contains a merge tag that isn't recognized.",
  },

  // ── Update template ──
  '180701': {
    httpStatus: 400,
    message: "This template couldn't be saved because it may contain flagged content. Please revise and try again.",
  },
  '180702': {
    httpStatus: 400,
    message: "Add the {{phone number}} merge tag to your template's body before saving.",
  },
  '180703': {
    httpStatus: 400,
    message: "This template contains a merge tag that isn't recognized.",
  },
};
